import { Vehicle } from './vehicle';
import { GPSFrameEventType } from './gps-frame-event-type';
import { GPSState } from './gps-state';
import { Expose, Type } from 'class-transformer';
import { Positionable } from 'src/app/map/model/positionable';
import { Nameable } from 'src/app/common/model/nameable';
import * as L from 'leaflet';
import { Carrier } from 'src/app/management/model/carrier';
import { RECEIVE_TIME_SC } from 'src/app/common/const';
import { EmergencyState } from './emergency/emergency-state';
import { PositionStatus } from './position-status';

export const ALCOHOL_LOCK_STATE = 'alcoholLockState';
export const ROUTE_CONTROL_STATE = 'routeControlState';

export class GPSFrame implements Positionable, Nameable {
  @Expose({ name: 'frame_id' })
  id: number;
  @Expose({ name: 'vehicle' })
  @Type(() => Vehicle)
  vehicle: Vehicle;
  @Expose({ name: RECEIVE_TIME_SC })
  @Type(() => Date)
  receiveTime: Date;
  @Expose({ name: 'agid' })
  agid?: number;
  @Expose({ name: 'latitude' })
  latitude: number;
  @Expose({ name: 'longitude' })
  longitude: number;
  @Expose({ name: 'line' })
  line?: string;
  @Expose({ name: 'route_code' })
  routeCode?: string;
  @Expose({ name: 'route_id' })
  routeId?: number;
  @Expose({ name: 'people_count' })
  peopleCount?: number;
  @Expose({ name: 'previous_bus_stop' })
  previousBusStop?: string;
  @Expose({ name: 'next_bus_stop' })
  nextBusStop?: string;
  @Expose({ name: 'timetable_deviation' })
  timetableDeviation?: number;
  @Expose({ name: 'user_id' })
  userId?: string;
  @Expose({ name: 'direction_name' })
  directionName?: string;
  @Expose({ name: 'team' })
  team?: string;
  @Expose({ name: 'speed' })
  speed?: number;
  @Expose({ name: 'error' })
  error: boolean;
  @Expose({ name: 'event_type' })
  eventType?: GPSFrameEventType;
  @Expose({ name: 'network_address' })
  networkAddress?: string;
  @Expose({ name: 'gps_status' })
  gpsStatus?: GPSState;
  @Expose({ name: 'course' })
  course?: string;
  @Expose({ name: 'carrier' })
  carrier?: string[];
  @Expose({ name: 'carriers' })
  @Type(() => Carrier)
  carriers?: Carrier[];
  @Expose({ name: 'calc_prio' })
  calculatedPriority: number;
  @Expose({ name: 'obu_v2x_prio' })
  obuV2xPriority: number;
  @Expose({ name: 'deviceErrorSeverity'})
  deviceErrorSeverity?: 'ERROR' | 'WARNING' | 'OK' | 'UNKNOWN';
  busFill: number;
  @Type(() => CabinSwitches)
  cabinSwitches: CabinSwitches;
  emergencyState: EmergencyState;
  additionalData?: GPSFrameAdditionalData;
  // Trzy pola poniżej nie przychodzą z serwera - dopisuje je VehiclesMapComponent, bo filtr kolumnowy
  // dopasowuje po surowej właściwości wiersza (filterData czyta element[fieldName]).
  tracked?: boolean;
  detailsPinned?: boolean;
  // Komórka tabeli liczy ten stan na żywo, więc stempel na ramce może być o jedną porcję danych z tyłu.
  positionStatus?: PositionStatus;

  static getName(): string {
    return 'GPSFrame';
  }

  getClassName(): string {
    return GPSFrame.getName();
  }

  getPosition(): L.LatLng {
    return L.latLng(this.latitude, this.longitude);
  }

  isErrorBystrategy(strategy: GpsFrameShowErrorStrategy): boolean {
    switch (strategy) {
      case GpsFrameShowErrorStrategy.BY_FILED_ERROR:
        return this.error === true;

      case GpsFrameShowErrorStrategy.FIELD_ERROR_OR_ANY_CABIN_SWITCH_OFF: {
        const errorBySwitches = this.cabinSwitches != null ? this.cabinSwitches.isAnyOff() : false;
        return this.error === true || errorBySwitches;
      }

      case GpsFrameShowErrorStrategy.BY_DEVICE_ERROR_SEVERITY:
        return this.deviceErrorSeverity === 'ERROR' || this.deviceErrorSeverity === 'WARNING';

      default:
        return false;
    }
  }

  hasSeverityIcon(): boolean {
    return this.deviceErrorSeverity === 'ERROR' || this.deviceErrorSeverity === 'WARNING';
  }

  getSeverityIconPath(): string | null {
    switch (this.deviceErrorSeverity) {
      case 'ERROR':
        return '/assets/images/red_error.svg';
      case 'WARNING':
        return '/assets/images/yellow_warning.svg';
      default:
        return null;
    }
  }

}

export class CabinSwitches {
  @Expose({ name: 'hebel_lcd' })
  public readonly lcdSwitch?: boolean;
  @Expose({ name: 'hebel_led' })
  public readonly ledSwitch?: boolean;
  @Expose({ name: 'hebel_irma' })
  public readonly irmaSwitch?: boolean;

  public isAnyOff(): boolean {
    return (this.lcdSwitch != null && !this.lcdSwitch) ||
      (this.ledSwitch != null && !this.ledSwitch) ||
      (this.irmaSwitch != null && !this.irmaSwitch);
  }

}

export enum GpsFrameShowErrorStrategy {
  BY_FILED_ERROR = 'by_filed_error',
  FIELD_ERROR_OR_ANY_CABIN_SWITCH_OFF = 'field_error_or_any_cabin_switch_off',
  BY_DEVICE_ERROR_SEVERITY = 'by_device_error_severity'
}

export class GPSFrameAdditionalData {
  devId?: string;
  deviceType?: string;
  deviceError?: boolean;
}
